import React, { useEffect } from "react";
import { useNotifications } from "../hooks/useNotifications";
import { AlertCircle, Bell, Check, CheckCheck } from "lucide-react";

function NotificationIcon({ kind }) {
  const icons = {
    collaboration: "bg-violet-100 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400",
    offer: "bg-indigo-100 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400",
    commission: "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400",
    invoice: "bg-amber-100 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400",
    message: "bg-blue-100 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400",
    wallet: "bg-cyan-100 text-cyan-600 dark:bg-cyan-500/10 dark:text-cyan-400",
    system: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  };
  return (
    <div className={`p-2 rounded-xl ${icons[kind] || icons.system}`}>
      <Bell size={18} />
    </div>
  );
}

export default function AdminMarketplaceNotifications() {
  const { notifications, summary, loading, error, fetchNotifications, fetchSummary, markRead, markAllRead } = useNotifications();

  useEffect(() => {
    fetchNotifications();
    fetchSummary();
  }, [fetchNotifications, fetchSummary]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin dark:border-indigo-500/20 dark:border-t-indigo-400" />
          <p className="text-sm text-slate-500 dark:text-slate-500">Loading notifications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center dark:bg-rose-500/10 dark:border-rose-500/30">
        <AlertCircle size={32} className="mx-auto text-rose-500 mb-2 dark:text-rose-400" />
        <p className="text-rose-700 font-medium dark:text-rose-400">Failed to load notifications</p>
        <button onClick={() => fetchNotifications()} className="mt-3 px-4 py-2 bg-rose-600 text-white rounded-lg text-sm hover:bg-rose-700 dark:bg-rose-600 dark:hover:bg-rose-700">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Notifications</h1>
          <p className="text-sm text-slate-500 mt-0.5 dark:text-slate-500">Stay updated with marketplace activity.</p>
        </div>
        {summary?.unread > 0 && (
          <button
            onClick={markAllRead}
            className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <CheckCheck size={16} />
            Mark All Read
          </button>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-500">Total</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{summary.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-500">Unread</p>
            <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{summary.unread}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-500">Read</p>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{(summary.total || 0) - (summary.unread || 0)}</p>
          </div>
        </div>
      )}

      {/* Notifications List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden dark:bg-slate-900 dark:border-slate-800">
        {notifications.length === 0 ? (
          <div className="p-12 text-center">
            <Bell size={48} className="mx-auto text-slate-300 mb-3 dark:text-slate-700" />
            <p className="text-slate-500 text-sm dark:text-slate-500">No notifications yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {notifications.map((notif) => (
              <div
                key={notif.id}
                className={`p-4 flex items-start gap-4 hover:bg-slate-50 transition dark:hover:bg-slate-800 ${
                  !notif.is_read ? "bg-indigo-50/30 dark:bg-indigo-500/5" : ""
                }`}
              >
                <NotificationIcon kind={notif.kind} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-slate-900 dark:text-slate-100">{notif.title}</p>
                  <p className="text-sm text-slate-600 mt-0.5 dark:text-slate-400">{notif.message}</p>
                  <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">
                    {new Date(notif.created_at).toLocaleString()}
                  </p>
                </div>
                {!notif.is_read && (
                  <button
                    onClick={() => markRead(notif.id)}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-indigo-600 dark:hover:bg-slate-800 dark:text-slate-500 dark:hover:text-indigo-400"
                    title="Mark as read"
                  >
                    <Check size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
